import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { GlassCard } from '@/components/GlassCard';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Share2, Sparkles } from 'lucide-react';
import { FullPageLoader } from '@/components/Loaders';
import { toast } from 'sonner';

export default function MyQRPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [qr, setQr] = useState(null);

  useEffect(() => {
    api.get('/users/me/qr').then((r) => setQr(r.data));
  }, []);

  const share = async () => {
    if (!qr) return;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'My Thirrsty QR', text: qr.payload, url: window.location.href });
      } else {
        await navigator.clipboard.writeText(qr.payload);
        toast.success('QR payload copied to clipboard');
      }
    } catch {}
  };

  return (
    <div className="min-h-screen th-hero-radial">
      <header className="sticky top-0 z-30 bg-[#07070A]/85 backdrop-blur-xl border-b border-white/5">
        <div className="mx-auto flex max-w-2xl items-center gap-2 px-4 py-3">
          <Button variant="ghost" size="icon" onClick={() => nav(-1)} className="text-white hover:bg-white/10" data-testid="my-qr-back-button">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="font-display text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>My QR Code</h1>
          <div className="ml-auto"><Logo size={96} withText={false} /></div>
        </div>
      </header>
      <main className="mx-auto max-w-md space-y-4 px-4 py-6">
        {!qr ? (
          <FullPageLoader />
        ) : (
          <>
            <GlassCard className="p-6 text-center">
              <p className="text-xs uppercase tracking-wider text-[#F6B24A]">Show at the venue</p>
              <h2 className="mt-1 font-display text-2xl font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>{user?.display_name}</h2>
              <div className="mt-5 rounded-3xl border border-white/12 bg-white/5 p-5 backdrop-blur-xl shadow-[0_18px_60px_rgba(0,0,0,0.55)]">
                <div className="rounded-2xl bg-white p-4">
                  <img
                    src={qr.data_url}
                    alt="Your Thirrsty QR"
                    className="w-full max-w-[280px] mx-auto"
                    data-testid="profile-qr-code"
                  />
                </div>
              </div>
              <p className="mt-3 break-all font-mono text-xs text-white/50" data-testid="profile-qr-payload">{qr.payload}</p>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <Button onClick={share} variant="outline" className="h-11 rounded-xl border-white/12 bg-white/5 text-white hover:bg-white/10" data-testid="my-qr-share-button">
                  <Share2 className="mr-2 h-4 w-4" /> Share
                </Button>
                <Button onClick={() => nav('/discover')} className="h-11 rounded-xl bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065] font-semibold" data-testid="my-qr-find-venues-button">
                  <Sparkles className="mr-2 h-4 w-4" /> Find Venues
                </Button>
              </div>
            </GlassCard>

            <GlassCard className="p-4 text-sm text-white/70">
              <p className="font-display text-white font-semibold mb-1" style={{ fontFamily: 'Space Grotesk' }}>How it works</p>
              <ol className="list-decimal pl-5 space-y-1 text-xs">
                <li>Walk into any Thirrsty venue.</li>
                <li>Show this QR at the bar or door.</li>
                <li>Staff scans it — your discount is applied and your check-in recorded.</li>
              </ol>
            </GlassCard>
          </>
        )}
      </main>
    </div>
  );
}
